/**
 * inAppBrowser
 *
 * Best-effort detection of mobile devices and in-app browsers (the webview an email/social app
 * opens links in, rather than the system browser), based on navigator.userAgent.
 *
 * This is fundamentally unreliable and must be treated as a heuristic, not a source of truth:
 *  - iOS Gmail and Outlook present a UA string indistinguishable from plain mobile Safari, so
 *    isInAppBrowser() CANNOT detect those two on iOS. There is no known workaround.
 *  - Gmail/Outlook on Android are believed to route links through their own in-app browser too,
 *    but no stable, documented UA signature for either was available when this was written -
 *    deliberately left undetected here rather than guessing a token that might not exist. Add a
 *    signature once confirmed against a real device (see the sign-in in-app-browser test matrix).
 *  - Any signature below can change if the app updates its webview/UA string.
 *
 * Plain CommonJS (not the `import`/`export` used elsewhere in utils/) so this file - and its
 * behavior - can be verified with `node utils/inAppBrowser.test.js`, with no test framework or
 * build step required, since none exists in this repo yet. Consuming code can still
 * `import { isInAppBrowser } from 'utils/inAppBrowser'` as usual; Babel/webpack interop resolves
 * named imports from a CommonJS `exports.x` module the same way it does for third-party packages.
 */

const IN_APP_BROWSER_PATTERNS = [
  { name: 'facebook', pattern: /FBAN|FBAV/i },
  { name: 'instagram', pattern: /Instagram/i },
  { name: 'linkedin', pattern: /LinkedInApp/i },
  { name: 'twitter', pattern: /Twitter/i },
  { name: 'line', pattern: /\bLine\//i },
  { name: 'wechat', pattern: /MicroMessenger/i },
  { name: 'tiktok', pattern: /musical_ly|TikTok/i },
  { name: 'pinterest', pattern: /Pinterest/i },
  { name: 'snapchat', pattern: /Snapchat/i },
  // Reported for Teams' mobile in-app browser; unverified against a real device by us.
  { name: 'teams', pattern: /\bTeams\// },
];

// A bare Android WebView (Chrome rendering engine embedded in a native app) tags itself with this
// token even when the host app doesn't add its own. Catches in-app browsers not named above.
const ANDROID_WEBVIEW_PATTERN = /; ?wv\)/i;

const isMobileUserAgent = (userAgent) => !!userAgent && /Android|iPhone|iPad|iPod/i.test(userAgent);

const isIosUserAgent = (userAgent) => !!userAgent && /iPhone|iPad|iPod/i.test(userAgent);

const isAndroidUserAgent = (userAgent) => !!userAgent && /Android/i.test(userAgent);

/**
 * getInAppBrowserName
 *
 * @param userAgent - navigator.userAgent (or a request's user-agent header)
 * @returns {string|null} a short identifier for the detected in-app browser, or null if none
 * matched. Absence of a match does NOT mean the user is in the system browser - see the iOS
 * Gmail/Outlook caveat above.
 */
const getInAppBrowserName = (userAgent) => {
  if (!userAgent) return null;

  const named = IN_APP_BROWSER_PATTERNS.find(({ pattern }) => pattern.test(userAgent));
  if (named) return named.name;

  if (isAndroidUserAgent(userAgent) && ANDROID_WEBVIEW_PATTERN.test(userAgent)) return 'android-webview';

  return null;
};

const isInAppBrowser = (userAgent) => getInAppBrowserName(userAgent) !== null;

module.exports = {
  isMobileUserAgent,
  isIosUserAgent,
  isAndroidUserAgent,
  getInAppBrowserName,
  isInAppBrowser,
};
